import { zodResolver } from '@hookform/resolvers/zod';
import DeleteIcon from '@mui/icons-material/Delete';
import PasswordIcon from '@mui/icons-material/Password';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { AccountUserDetailDTO, AccountUserUpdateDtoSchema, RoleEnum } from '_lib/api/apiTypes.shim';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { useUpdatePasswordDialog } from '_lib/hooks/UpdatePasswordDialogHook';
import { useIsOverflowing } from '_lib/hooks/useIsOverflowing';

import { extractAllErrors } from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AuthUser } from '../auth/auth.types';
import { AuthContext } from '../auth/authContext';
import { NavigationBlocker } from '../NavigationBlocker/NavigationBlocker';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';

import { AccountUserForm } from './AccountUserForm';

type AccountUserUpdateProps = {
  accountUserId: string;
  authUser: AuthUser | undefined;
  onSave?: (accountUser: AccountUserDetailDTO) => void;
  isFetchingFromProps?: boolean;
};

export const AccountUserUpdate = (props: AccountUserUpdateProps) => {
  const { accountUserId, authUser, onSave, isFetchingFromProps } = props;
  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();

  const scrollTriggerTarget = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useIsOverflowing(scrollTriggerTarget);

  const updatePasswordDialog = useUpdatePasswordDialog();

  const api = useContext(ApiContext);
  const { authUserCacheResource } = useContext(AuthContext);

  const accountUserPutMutation = api.accountUsers.usePut();
  const accountUserDeleteMutation = api.accountUsers.useDelete();
  const accountUserReactivateMutation = api.accountUsers.useReactivate();

  const accountUserQuery = api.accountUsers.useGetById({ id: accountUserId });
  const accountUserData = accountUserQuery?.data;

  const accountUserFormContext = useForm({
    resolver: zodResolver(AccountUserUpdateDtoSchema.required()),
    mode: 'onChange',
    shouldFocusError: true
  });

  useEffect(() => {
    if (accountUserData) {
      accountUserFormContext.reset(accountUserData);
    }
  }, [accountUserData, accountUserFormContext]);

  const handleSave = apiSubmitHandler(
    accountUserFormContext.handleSubmit,
    accountUserFormContext.setError,
    async (data, { setFormErrors }) => {
      setNonFieldErrors(undefined);
      setAllErrors(undefined);
      try {
        const result = await accountUserPutMutation.put({ id: accountUserId, data });
        if (result.id === authUser?.id) {
          await authUserCacheResource.set(result).catch(() => {});
        }
        accountUserFormContext.reset(result);
        enqueueSnackbar('User saved');
        onSave?.(result);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setNonFieldErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors, allErrors);
      }
    }
  );

  const handleDeactivate = async () => {
    try {
      await accountUserDeleteMutation.deleteItem({ id: accountUserId });
      enqueueSnackbar('User deactivated');
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
      }
    }
  };

  const handleReactivate = async () => {
    try {
      await accountUserReactivateMutation.reactivate({ id: accountUserId });
      enqueueSnackbar('User reactivated');
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
      }
    }
  };

  if (accountUserQuery.error) {
    return <RouteErrorBoundary disableAppBar errorTitle='Error Fetching Account User' />;
  }

  const isFetchingOrPending =
    isFetchingFromProps ||
    accountUserQuery.isFetching ||
    accountUserPutMutation.isPending ||
    accountUserDeleteMutation.isPending ||
    accountUserReactivateMutation.isPending;

  const isEditingOwnProfile = accountUserData?.id === authUser?.id;

  if (!isFetchingOrPending && !accountUserData) {
    return <RouteErrorBoundary errorTitle='Account User Not Found' />;
  }

  return (
    <>
      <NavigationBlocker
        getBlockCondition={() => Object.keys(accountUserFormContext.formState.dirtyFields).length > 0}
      />
      <ActionBar
        actions={
          <>
            <Button
              disableFocusRipple
              text='Update Password'
              variant='text'
              onClick={async () => {
                if (accountUserData) await updatePasswordDialog.show({ user: accountUserData });
              }}
              startIcon={<PasswordIcon />}
              sx={{ textTransform: 'none' }}
              disabled={
                isFetchingOrPending ||
                !accountUserData ||
                !accountUserData.isActive ||
                (!!authUser?.accountId && authUser?.role === RoleEnum.USER && accountUserData.role !== RoleEnum.USER)
              }
            />
            {!isEditingOwnProfile && (
              <Button
                disableFocusRipple
                text={accountUserData?.isActive ? 'Deactivate' : 'Reactivate'}
                variant='text'
                onClick={accountUserData?.isActive ? handleDeactivate : handleReactivate}
                startIcon={accountUserData?.isActive ? <DeleteIcon /> : <SettingsBackupRestoreIcon />}
                sx={{ textTransform: 'none' }}
                disabled={
                  isFetchingOrPending ||
                  (!!authUser?.accountId && authUser?.role === RoleEnum.USER && accountUserData?.role !== RoleEnum.USER)
                }
              />
            )}
          </>
        }
      />
      <Divider />
      <FlexBox
        sx={(theme) => ({
          backgroundColor: theme.palette.background.default,
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          position: 'relative'
        })}>
        <BackdropSpinner open={isFetchingOrPending} />
        {!!authUser && !!Object.keys(accountUserFormContext.getValues()).length && (
          <>
            <Stack padding={2} useFlexGap spacing={2} overflow='auto' ref={scrollTriggerTarget}>
              {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
              {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
              {!accountUserQuery.isFetching && accountUserData?.isActive === false && (
                <WarningAlert>
                  <Typography>Account user is inactive</Typography>
                </WarningAlert>
              )}
              <AccountUserForm
                accountUserFormContext={accountUserFormContext}
                authUser={authUser}
                isInactive={!accountUserData?.isActive}
                isEditingOwnProfile={isEditingOwnProfile}
              />
            </Stack>
            {isOverflowing && <Divider />}
            <FlexBox sx={{ paddingX: 2, paddingY: 1, marginLeft: 'auto' }}>
              <Button
                disabled={!accountUserData?.isActive}
                onClick={handleSave}
                text='Save'
                variant='contained'
                size='small'
                sx={{ textTransform: 'none' }}
              />
            </FlexBox>
          </>
        )}
      </FlexBox>
      {updatePasswordDialog.element}
    </>
  );
};
